import React, { useCallback, useEffect, useRef } from 'react';

import arrowUpLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-up-large';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import FloatingButton from '@amzn/meridian/floating-button';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { SUBMIT_FEATURE_REQUEST_AND_BUG_URL } from 'src/config/ExternalLinks';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { getTaskQueryOptions } from 'src/services/tasks/taskQueries';
import {
  setIsCancelModalOpen,
  setPageProgress,
  setSelectedLimitations,
  setSelectedRestrictions,
  setSelectedTasks,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { closeAllToastsForMessage, createToastIfNoneForMessage } from 'src/store/toastsSlice';
import { JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';
import {
  getEffectiveShiftLength,
  nonPhysicalTasksToLimitationOptionsList,
  regionTasksAndGroupsPayloadToRestrictionOptionsList,
  selectedRestrictionOptionsToJmrTaskList,
  validateSelectedLimitations,
  validateSelectedRestrictions,
} from 'src/utils/restrictionUtils';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJmrPageTitle, NewJMRPageWrapper } from '../../pageLayout/pageLayout';
import PlacementCountAlert from '../../placementCountAlert/placementCountAlert';
import NonPhysicalLimitations from '../nonPhysicalLimitations/nonPhysicalLimitations';

import AllTasksSelector from './allTasksSelector';

import PhysicalRestrictions from './physicalRestrictions/physicalRestrictions';

// **** Constants **** //

// Exporting for testing
export const TOAST_MESSAGE = 'Please see the errors below, and try clicking "Next" again';

// **** Functional Components **** //

/**
 * Main component.
 */
const RestrictionsPageNewExperience = () => {
  const dispatch = useAppDispatch();
  const { selectedRestrictions, selectedLimitations, jmrFlowType, backgroundFormData } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const { featureFlags } = useAppSelector((state) => state.user);
  const shiftLengthStr = String(getEffectiveShiftLength(backgroundFormData));
  const isRestrictionsPageReadOnly = jmrFlowType === JmrFlowTypes.UpdateDates;
  const headerRef = useRef<HTMLElement>(null);

  const {
    data: limitationOptions,
    isSuccess: isLimitationsQuerySuccess,
    isLoading: isLimitationsLoading,
  } = useQuery({
    ...getTaskQueryOptions.nonPhysicalTasksByRegion(backgroundFormData.site?.regionId),
    enabled: !!backgroundFormData.site?.regionId,
    select: nonPhysicalTasksToLimitationOptionsList,
  });

  const {
    data: restrictionOptions,
    isSuccess: isRestrictionsQuerySuccess,
    isLoading: isRestrictionsLoading,
  } = useQuery({
    ...getTaskQueryOptions.regionTaskGroups(backgroundFormData.site?.regionId),
    enabled: !!backgroundFormData.site?.regionId,
    select: regionTasksAndGroupsPayloadToRestrictionOptionsList,
  });
  const nonPhysicalLimitationsEnabled =
    featureFlags.NON_PHYSICAL_LIMITATIONS && backgroundFormData.site?.nonPhysicalLimitationsEnabled;

  // All dropdown selector options for Restrictions
  const allFormattedRestrictionTaskOptions = (restrictionOptions || []).map((o) => ({
    label: o.label,
    value: o.entityId,
    key: o.entityId,
  }));

  const allFormattedNonPhysicalTaskOptions = (limitationOptions || []).map((o) => ({
    label: o.nonPhysicalLimitationTask.name,
    value: o.nonPhysicalLimitationTaskId.toString(),
    key: o.nonPhysicalLimitationTaskId.toString(),
  }));

  const goToJobMatchesPage = useCallback(() => {
    if (isRestrictionsPageReadOnly) {
      dispatch(setPageProgress(JmrPageProgress.JobMatches));
      return;
    }

    const { isValid: areRestrictionsValid, updatedList: updatedRestrictions } =
      validateSelectedRestrictions(selectedRestrictions);
    const { isValid: areLimitationsValid, updatedList: updatedLimitations } =
      validateSelectedLimitations(selectedLimitations);
    dispatch(setSelectedRestrictions(updatedRestrictions));
    dispatch(setSelectedLimitations(updatedLimitations));

    if (areRestrictionsValid && areLimitationsValid) {
      dispatch(setSelectedTasks(selectedRestrictionOptionsToJmrTaskList(selectedRestrictions, shiftLengthStr)));
      dispatch(closeAllToastsForMessage(TOAST_MESSAGE));
      dispatch(setPageProgress(JmrPageProgress.JobMatches));
    } else {
      dispatch(
        createToastIfNoneForMessage({
          type: 'error',
          message: TOAST_MESSAGE,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  }, [selectedLimitations, selectedRestrictions, isRestrictionsPageReadOnly, shiftLengthStr]);

  const setLimitations = (entityIds: string[]) => {
    if (!isLimitationsQuerySuccess) return;
    dispatch(
      setSelectedLimitations(
        entityIds.map(
          (id) =>
            selectedLimitations.find((l) => l.nonPhysicalLimitationTaskId.toString() === id) ??
            limitationOptions.find((l) => l.nonPhysicalLimitationTaskId.toString() === id)!,
        ),
      ),
    );
  };

  const setRestrictions = (entityIds: string[]) => {
    if (!isRestrictionsQuerySuccess) return;
    dispatch(
      setSelectedRestrictions(
        entityIds.map(
          (entityId) =>
            selectedRestrictions.find((r) => r.entityId === entityId) ??
            restrictionOptions.find((r) => r.entityId === entityId)!,
        ),
      ),
    );
  };

  // clear stale limitations
  useEffect(() => {
    if (!nonPhysicalLimitationsEnabled && selectedLimitations.length) {
      dispatch(setSelectedLimitations([]));
    }
  }, [nonPhysicalLimitationsEnabled, selectedLimitations]);

  return (
    <NewJMRPageWrapper>
      <NewJMRPageMainBody xs="85%" md="75%" lg="60%">
        <Column spacing="450" spacingInset="300">
          <PlacementCountAlert />
          <NewJmrPageTitle
            title="Add and Edit Restriction(s)"
            subtitle={
              nonPhysicalLimitationsEnabled ? (
                <>
                  Please enter all physical restrictions and non-physical limitations as documented in the associate's
                  medical paperwork (RFI, APF, WSR, etc.). Non-physical limitations do not affect the job match results
                  and are for record keeping only. Need to request a new non-physical limitation option? Submit a{' '}
                  <Link type="secondary" target="_blank" href={SUBMIT_FEATURE_REQUEST_AND_BUG_URL}>
                    SIM ticket
                  </Link>
                  .
                </>
              ) : (
                <>Please add and enter all restrictions as denoted within the associates RFI.</>
              )
            }
          />

          {isRestrictionsLoading || isLimitationsLoading ? (
            <LoadingSpinner />
          ) : (
            <span style={{ pointerEvents: isRestrictionsPageReadOnly ? 'none' : 'auto' }}>
              <Row spacing="400" widths={['fill', 'fill']} ref={headerRef}>
                {isRestrictionsQuerySuccess ? (
                  <AllTasksSelector
                    label="Physical Restrictions"
                    isDisabled={isRestrictionsPageReadOnly}
                    availableOptions={allFormattedRestrictionTaskOptions}
                    selectedOptions={selectedRestrictions.map((item) => item.entityId)}
                    onSelectTasks={setRestrictions}
                  />
                ) : null}
                {isLimitationsQuerySuccess && nonPhysicalLimitationsEnabled ? (
                  <AllTasksSelector
                    label="Non-Physical Limitations"
                    dataTestId="new-jmr__limitations__all-tasks__dropdown-selector"
                    isDisabled={isRestrictionsPageReadOnly}
                    availableOptions={allFormattedNonPhysicalTaskOptions}
                    selectedOptions={selectedLimitations.map((item) => item.nonPhysicalLimitationTaskId.toString())}
                    onSelectTasks={setLimitations}
                  />
                ) : null}
              </Row>

              <Column spacingInset="400 none" spacing="400">
                {isRestrictionsQuerySuccess ? (
                  <PhysicalRestrictions restrictionOptionsList={restrictionOptions} />
                ) : null}
                {nonPhysicalLimitationsEnabled && isLimitationsQuerySuccess ? <NonPhysicalLimitations /> : null}
              </Column>
            </span>
          )}
        </Column>
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter
        leftSideContent={
          <Column spacing="200">
            {isRestrictionsQuerySuccess && <Text>{selectedRestrictions.length} Physical Restriction(s)</Text>}
            {nonPhysicalLimitationsEnabled && isLimitationsQuerySuccess && (
              <Text>{selectedLimitations.length} Non-Physical Limitation(s)</Text>
            )}
          </Column>
        }
      >
        {selectedLimitations.length + selectedRestrictions.length > 2 ? (
          <FloatingButton
            size="medium"
            className="new-jmr__jump-to-top"
            position="absolute"
            circular
            onClick={() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            type="primary"
            alignmentHorizontal="end"
          >
            <Icon tokens={arrowUpLargeTokens} />
          </FloatingButton>
        ) : null}

        <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
          Cancel
        </Button>
        <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.Background))}>
          Previous
        </Button>
        <Button data-cy="new-jmr__restrictions__next" onClick={goToJobMatchesPage}>
          Next
        </Button>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

// **** Export **** //

export default RestrictionsPageNewExperience;
